import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import {
  getMyNetwork, getShareables, membershipStatus, membershipCancel, membershipConfirm,
  supporterMe, submitSupporter, getMyDimensions, getMyCollection, resendVerification, money, formatApiErrorDetail,
} from "../../lib/api";

const label = "font-mono text-[10px] tracking-[0.25em] text-white/40 block mb-2";
const input = "w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-2.5 text-white transition-colors";

export function MyNetworkSection({ user }) {
  const [net, setNet] = useState(null);
  const [shareables, setShareables] = useState([]);
  const [path, setPath] = useState("/");
  useEffect(() => {
    getMyNetwork().then(setNet).catch(() => {});
    getShareables().then((s) => { setShareables(s); if (s[0]) setPath(s[0].path); }).catch(() => {});
  }, []);
  if (!net) return null;
  const link = `${window.location.origin}${path}${path.includes("?") ? "&" : "?"}ref=${net.code}`;
  const copy = () => { navigator.clipboard.writeText(link); toast.success("Referral link copied."); };

  return (
    <div data-testid="section-network">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
        {[["Points", net.points], ["Clicks", net.clicks], ["Referred", net.signups], ["Verified", net.verified]].map(([l, v]) => (
          <div key={l} className="border border-white/10 p-5"><p className="font-serif text-3xl text-gold">{v}</p><p className="font-mono text-[8px] tracking-[0.2em] text-white/40 mt-1 uppercase">{l}</p></div>
        ))}
      </div>
      <p className={label}>MY REFERRAL CODE</p>
      <p className="font-mono text-2xl tracking-[0.3em] text-gold" data-testid="my-referral-code">{net.code}</p>
      {!user.emailVerified && (
        <p className="text-white/40 text-xs mt-2">Verify your email to fully activate your network. <button onClick={() => resendVerification().then(() => toast.success("Verification email sent."))} className="text-gold">Resend link</button></p>
      )}
      <div className="mt-8 grid md:grid-cols-2 gap-8">
        <div>
          <label className={label}>SHARE MUSIC — GENERATE A LINK</label>
          <select value={path} onChange={(e) => setPath(e.target.value)} data-testid="share-select"
            className={`${input} bg-[#0a0a0a]`}>
            {shareables.map((s) => <option key={s.path} value={s.path}>{s.label}</option>)}
          </select>
          <p className="font-mono text-[10px] text-white/50 mt-4 break-all" data-testid="share-link">{link}</p>
          <button onClick={copy} data-testid="share-copy" className="mt-4 border border-gold/60 text-gold font-mono text-[10px] tracking-[0.25em] px-6 py-3 hover:bg-gold hover:text-black transition-colors">COPY LINK</button>
        </div>
        <div>
          <label className={label}>QR CODE</label>
          <div className="bg-white p-3 inline-block"><QRCodeSVG value={link} size={128} /></div>
        </div>
      </div>
      <div className="mt-10">
        <p className={label}>REFERRAL HISTORY</p>
        {net.referred.length === 0 && <p className="text-white/40 text-sm">No referred members yet. Share a link to begin.</p>}
        {net.referred.map((r, i) => (
          <div key={i} className="flex justify-between py-2.5 border-t border-white/5 text-xs">
            <span className="text-white/70">{r.email}</span>
            <span className={r.verified ? "text-gold" : "text-white/30"}>{r.verified ? `VERIFIED · +${r.points} PTS` : "AWAITING VERIFICATION"}</span>
          </div>
        ))}
        <p className="font-mono text-[8px] tracking-[0.2em] text-white/25 mt-6">POINTS ARE INTERNAL REWARDS. REDEMPTION OPTIONS ARRIVE SOON — NEVER CASH.</p>
      </div>
    </div>
  );
}

export function InnerCircleSection() {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const load = () => membershipStatus().then(setStatus).catch(() => {});
    if (params.get("membership") === "success" && params.get("session_id")) {
      membershipConfirm({ session_id: params.get("session_id") }).then(load).catch(load);
    } else load();
  }, []);
  if (!status) return null;

  const cancel = async () => {
    if (!window.confirm("Cancel your Inner Circle membership? It stays active until the period ends. Purchased music remains yours forever.")) return;
    try { const r = await membershipCancel(); toast.success(r.note); membershipStatus().then(setStatus); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div data-testid="section-inner-circle">
      {status.active ? (
        <div className="border border-gold/40 p-8">
          <p className="font-mono text-[10px] tracking-[0.35em] text-gold">ACTIVE — {status.planName?.toUpperCase()}</p>
          <p className="font-serif text-3xl text-white mt-4">You are inside the circle.</p>
          <p className="text-white/50 text-sm mt-3">
            {status.price ? `${money(status.price, "usd")}/month · ` : ""}
            {status.cancelAtPeriodEnd ? "Ends" : "Renews"} {status.currentPeriodEnd ? new Date(status.currentPeriodEnd).toLocaleDateString() : "—"}
          </p>
          {!status.cancelAtPeriodEnd && (
            <button onClick={cancel} data-testid="membership-cancel" className="mt-6 border border-white/20 text-white/50 font-mono text-[10px] tracking-[0.25em] px-6 py-3 hover:border-red-500/50 hover:text-red-400 transition-colors">CANCEL MEMBERSHIP</button>
          )}
        </div>
      ) : (
        <div className="border border-white/10 p-8">
          <p className="font-serif text-2xl text-white/70">You are not an Inner Circle member.</p>
          <p className="text-white/40 text-sm mt-3 max-w-md">Exclusive content, early access and member pricing — separate from the free mailing list. Your purchased music is never affected.</p>
          <Link to="/inner-circle" data-testid="membership-join-link" className="inline-block mt-6 bg-gold text-black font-mono text-[10px] tracking-[0.25em] px-8 py-3.5 hover:bg-white transition-colors">EXPLORE MEMBERSHIP</Link>
        </div>
      )}
    </div>
  );
}

export function SupporterSection() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ displayName: "", platform: "", handle: "", link: "", designation: "Supporter", visibility: "public" });
  const load = () => supporterMe().then((d) => { setData(d); if (d.profile) setForm({ ...form, ...d.profile }); }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  if (!data) return null;

  const submit = async (e) => {
    e.preventDefault();
    try { await submitSupporter(form); toast.success("Submitted for review. An administrator approves public profiles."); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div data-testid="section-supporter">
      {!data.eligible ? (
        <div className="border border-white/10 p-8">
          <p className="font-serif text-2xl text-white/70">Supporter recognition unlocks with a qualifying purchase.</p>
          <Link to="/store" className="inline-block mt-5 font-mono text-[10px] tracking-[0.25em] text-gold">VISIT THE STORE →</Link>
        </div>
      ) : (
        <>
          <div className="border border-gold/40 p-6 mb-8" data-testid="supporter-eligible-banner">
            <p className="font-mono text-[10px] tracking-[0.3em] text-gold">YOU ARE ELIGIBLE TO BE RECOGNIZED AS A MOTION & MEANING SUPPORTER</p>
            <p className="text-white/50 text-xs mt-2">Entirely optional. Only the fields you approve appear publicly — never your email, orders or billing details.</p>
            {data.profile && <p className="font-mono text-[9px] text-white/40 mt-3">STATUS: {data.profile.approvalStatus?.toUpperCase()} · VISIBILITY: {data.profile.visibility?.toUpperCase()}</p>}
          </div>
          <form onSubmit={submit} className="grid md:grid-cols-2 gap-6 max-w-2xl">
            <div><label className={label}>DISPLAY NAME</label>
              <input required value={form.displayName} onChange={(e) => set("displayName", e.target.value)} data-testid="supporter-display-name" className={input} /></div>
            <div><label className={label}>DESIGNATION</label>
              <input value={form.designation} onChange={(e) => set("designation", e.target.value)} className={input} /></div>
            <div><label className={label}>PLATFORM</label>
              <select value={form.platform} onChange={(e) => set("platform", e.target.value)} className={`${input} bg-[#0a0a0a]`}>
                {["", "instagram", "x", "tiktok", "youtube", "facebook", "threads", "website", "other"].map((p) => <option key={p} value={p}>{p || "none"}</option>)}
              </select></div>
            <div><label className={label}>HANDLE</label>
              <input value={form.handle} onChange={(e) => set("handle", e.target.value)} placeholder="@you" className={input} /></div>
            <div className="md:col-span-2"><label className={label}>APPROVED SOCIAL LINK (HTTPS)</label>
              <input value={form.link} onChange={(e) => set("link", e.target.value)} placeholder="https://…" className={input} /></div>
            <div><label className={label}>VISIBILITY</label>
              <select value={form.visibility} onChange={(e) => set("visibility", e.target.value)} data-testid="supporter-visibility" className={`${input} bg-[#0a0a0a]`}>
                <option value="public">PUBLIC</option><option value="hidden">HIDDEN</option>
              </select></div>
            <div className="flex items-end">
              <button type="submit" data-testid="supporter-submit" className="bg-gold text-black font-mono text-[10px] tracking-[0.25em] px-8 py-3.5 hover:bg-white transition-colors">
                {data.profile ? "UPDATE PROFILE" : "SUBMIT FOR APPROVAL"}
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
}

export function MyDimensionsSection() {
  const [dims, setDims] = useState([]);
  useEffect(() => { getMyDimensions().then(setDims).catch(() => {}); }, []);
  return (
    <div data-testid="section-dimensions">
      {dims.map((d) => (
        <div key={d.number} className="flex items-center justify-between border-t border-white/10 py-5">
          <div>
            <p className="font-serif text-xl text-white/85">{d.number} — {d.title}</p>
            <p className="font-mono text-[9px] tracking-[0.25em] text-white/35 mt-1">{d.theme || "UNKNOWN"}</p>
          </div>
          <span className={`font-mono text-[9px] tracking-[0.25em] border px-4 py-2 ${
            d.owned ? "text-gold border-gold/50" : d.restricted ? "text-red-500/70 border-red-500/30" : "text-white/30 border-white/10"}`}>
            {d.owned ? "OWNED" : d.restricted ? "RESTRICTED" : "NOT YET HELD"}
          </span>
        </div>
      ))}
    </div>
  );
}

export function CollectionSection({ orders }) {
  const [col, setCol] = useState(null);
  useEffect(() => { getMyCollection().then(setCol).catch(() => {}); }, []);
  const items = [];
  orders.forEach((o) => o.items.forEach((it) => items.push({ ...it, order: o.order_number, date: o.created_at })));
  const physical = items.filter((i) => i.fulfillment_type === "physical");
  const digital = items.filter((i) => i.fulfillment_type === "digital");
  const Group = ({ title, list }) => (
    <div className="mb-10">
      <p className={label}>{title}</p>
      {list.length === 0 && <p className="text-white/35 text-sm">Nothing here yet.</p>}
      {list.map((i, idx) => (
        <div key={idx} className="flex justify-between py-3 border-t border-white/5">
          <span className="text-white/80">{i.name} <span className="font-mono text-[9px] text-white/30 ml-2">×{i.quantity}</span></span>
          <span className="font-mono text-[9px] text-white/40">{i.order}</span>
        </div>
      ))}
    </div>
  );
  return (
    <div data-testid="section-collection">
      {col && (
        <div className="border border-gold/25 p-6 mb-10" data-testid="collection-progress">
          <p className={label}>THE MULTIDIMENSIONAL COLLECTION — YOUR PATH TO DIMENSION V</p>
          <div className="flex flex-wrap gap-2 mt-2">
            {["01", "02", "03", "04"].map((d) => {
              const s = (col.dimensions || {})[d] || {};
              return (
                <span key={d} data-testid={`collection-dim-${d}`}
                  className={`font-mono text-[9px] tracking-[0.2em] border px-4 py-2 ${
                    s.fulfilled ? "border-gold/60 text-gold" : s.owned ? "border-white/40 text-white/70" : "border-white/10 text-white/25"}`}>
                  DIM {d} {s.fulfilled ? "· OWNED" : s.owned ? "· IN FULFILLMENT" : "· NOT YET HELD"}
                </span>
              );
            })}
          </div>
          <p className="font-mono text-[9px] tracking-[0.2em] text-white/45 mt-5 leading-relaxed" data-testid="collection-status">
            {col.entitled ? "DIMENSION V ACCESS — ACTIVE."
              : col.credentialReady ? "A DIMENSION V CREDENTIAL AWAITS YOU — OPEN THE DIMENSION V TAB."
              : col.eligible ? "COLLECTION COMPLETE — YOUR CREDENTIAL IS BEING PREPARED."
              : col.ownedAll ? "COLLECTION COMPLETE — AWAITING ORDER FULFILLMENT."
              : "OWN ALL FOUR PHYSICAL DIMENSIONS — TOGETHER OR SEPARATELY — TO UNLOCK DIMENSION V."}
          </p>
        </div>
      )}
      <Group title="DIGITAL OWNERSHIP — PERMANENT" list={digital} />
      <Group title="PHYSICAL COLLECTION" list={physical} />
    </div>
  );
}
